/**
 * slice-3616 (RUSS 2026-09-11, demo): fire-and-forget crash report to the server log
 * (POST /api/client-errors — ClientErrorController writes one WARN line). Used by
 * AppErrorBoundary and the window-level error hooks in main.tsx. Never throws, never awaits,
 * de-duplicates identical messages within a session so a render loop cannot flood the log.
 */
const seen = new Set<string>();

export type ClientErrorReport = {
  message: string;
  stack?: string | undefined;
  componentStack?: string | undefined;
  source: 'boundary' | 'window.error' | 'unhandledrejection' | 'stale-chunk-reload' | 'ag-grid-filter-race';
};

/**
 * slice-4907 (demo log 30 Oct, Report Builder): "ResizeObserver loop completed with undelivered
 * notifications." is a benign browser notice (a resize callback caused another layout in the same
 * frame; nothing is lost or broken). Chrome/Firefox surface it as a window `error` event, so it
 * flooded the server log as a CLIENT ERROR. The older wording "ResizeObserver loop limit exceeded"
 * is the same notice.
 */
const BENIGN_MESSAGES: readonly RegExp[] = [
  /^ResizeObserver loop completed with undelivered notifications\.?$/,
  /^ResizeObserver loop limit exceeded$/,
];

export function reportClientError(r: ClientErrorReport): void {
  try {
    if (BENIGN_MESSAGES.some((re) => re.test(r.message.trim()))) return;
    const key = r.source + '|' + r.message.slice(0, 200);
    if (seen.has(key)) return;
    seen.add(key);
    let token: string | null = null;
    try {
      const raw = localStorage.getItem('ph10-auth');
      const parsed = raw ? (JSON.parse(raw) as { state?: { session?: { token?: string } } }) : null;
      token = parsed?.state?.session?.token ?? null;
    } catch {
      token = null;
    }
    void fetch('/api/client-errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
      },
      body: JSON.stringify({
        message: r.message.slice(0, 2000),
        stack: (r.stack ?? '').slice(0, 8000),
        componentStack: (r.componentStack ?? '').slice(0, 8000),
        url: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent.slice(0, 500),
        source: r.source,
      }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* reporting must never break the page */
  }
}
